import {
  FaCheckCircle,
  FaClipboardCheck,
  FaRegClock,
  FaShieldAlt,
  FaTasks,
  FaUserCheck,
  FaUserClock,
} from "react-icons/fa";
import Seo from "../../components/Seo";
import ServicePageTemplate from "../../components/ServicePageTemplate";
import { createBreadcrumbSchema } from "../../lib/seo";
import ServiceCTASection from "../../components/ServiceCTASection";

const seo = {
  title: "Medical Credentialing & Enrollment Services | PSP RCM",
  description:
    "Physicians Success Partners (PSP) delivers complete provider credentialing services, partnering closely with healthcare providers to streamline payer enrollment.",
  keywords:
    "credentialing service, provider enrollment, payer enrollment, recredentialing support",
  schema: [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      name: "Credentialing and Enrollment Services",
      url: "https://psprcm.us/services/credentialing-and-enrollment/",
      description:
        "Physicians Success Partners (PSP) delivers complete provider credentialing services, partnering closely with healthcare providers to streamline payer enrollment.",
      serviceType: "Credentialing & Enrollment",
      provider: {
        "@type": "Organization",
        name: "Physicians Success Partners",
        url: "https://psprcm.us/",
        logo: "https://psprcm.us/logo/main-logo.webp",
        telephone: "+1 6155536787",
        email: "info@psprcm.us",
        address: {
          "@type": "PostalAddress",
          streetAddress: "1616 Westgate Circle",
          addressLocality: "Brentwood",
          addressRegion: "TN",
          postalCode: "37027",
          addressCountry: "USA",
        },
      },
      areaServed: {
        "@type": "Country",
        name: "United States",
      },
      audience: {
        "@type": "MedicalAudience",
        audienceType: "Healthcare Providers",
      },
      availableChannel: {
        "@type": "ServiceChannel",
        serviceLocation: {
          "@type": "Place",
          address: { "@type": "PostalAddress", addressCountry: "USA" },
        },
        availableLanguage: ["English"],
      },
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: "Credentialing & Enrollment Solutions",
        itemListElement: [
          {
            "@type": "Offer",
            itemOffered: { "@type": "Service", name: "Provider Credentialing" },
          },
          {
            "@type": "Offer",
            itemOffered: { "@type": "Service", name: "Insurance Enrollment" },
          },
          {
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: "CAQH Profile Setup & Maintenance",
            },
          },
          {
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: "Re-Credentialing Services",
            },
          },
          {
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: "Payer Network Participation",
            },
          },
        ],
      },
    },
    createBreadcrumbSchema(
      "/services/credentialing-and-enrollment",
      "Credentialing and Enrollment Services",
    ),
  ],
};

export default function CredentialingEnrollment() {
  return (
    <>
      <Seo
        title={seo.title}
        metaTitle={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        canonicalPath="/services/credentialing-and-enrollment"
        schema={seo.schema}
      />
      <ServicePageTemplate
        banner={{
          heading: "Medical Credentialing & Enrollment Services",
          paragraph:
            "Physicians Success Partners (PSP) delivers complete provider credentialing services, partnering closely with healthcare providers to streamline payer enrollment, eliminate delays, and ensure your practice is approved and billing with every insurance network on time.",
          image: "/img/services-img/credentialing-and-enrollment.webp",
        }}
        grid={{
          kicker: "Provider Readiness",
          heading:
            "Provider Credentialing Services That Get Your Practice Approved Faster",
          description: `At Physicians Success Partners (PSP), our provider credentialing services are built around eliminating the bottlenecks that delay payer approval and interrupt your revenue cycle before it even begins.
            Our certified medical credentialing specialist team brings deep expertise across all major commercial payers, Medicare, Medicaid, and specialty networks. We manage every step of the credentialing and enrollment process from application preparation and document verification to follow-up and approval tracking, so your practice starts Medical billing without unnecessary delays or costly gaps in coverage.
            `,
          metrics: [
            {
              icon: <FaUserCheck />,
              value: "20+",
              label: "Years of Combined Credentialing Industry Experience",
              style: "metricPrimary",
            },
            {
              icon: <FaClipboardCheck />,
              value: "48 Hrs",
              label: "Average Application Turnaround Time",
              style: "metricAccent",
            },
            {
              icon: <FaUserClock />,
              value: "98%",
              label: "Credentialing Approval Success Rate",
              style: "metricLight",
            },
            {
              icon: <FaShieldAlt />,
              value: "100%",
              label: "HIPAA Compliance",
              style: "metricDark",
            },
          ],
          panelEyebrow: "Enrollment Oversight",
          panelHeading:
            "Optimized for Consistent and Accurate Credentialing Outcomes at Every Stage",
          assurances: [
            {
              icon: <FaTasks />,
              label: "Accurate Application Preparation & Submission",
            },
            {
              icon: <FaCheckCircle />,
              label: "Reliable Payer Follow-Up & Status Tracking",
            },
            {
              icon: <FaRegClock />,
              label: "Timely Network Approval & Enrollment Confirmation",
            },
          ],
        }}
        services={{
          heading:
            "PSP's Top Rated Provider Credentialing Services Group Is Here for Your Practice",
          description:
            "PSP's medical credentialing specialist team manages your entire credentialing and enrollment lifecycle from initial application to active payer status. We identify documentation gaps, prevent application rejections, and implement best practices that ensure compliant and accurate credentialing at every stage. Our structured insurance credentialing services approach helps practices of all sizes get enrolled faster, stay compliant, and avoid the revenue loss that comes with credentialing delays.",
          items: [
            {
              title: "Accurate Application Preparation",
              text: "Our provider credentialing services team organizes every application carefully, verifying licenses, certifications, malpractice history, and education records so submissions go out complete, accurate, and ready for fast payer approval the first time.",
            },
            {
              title: "Steadier Revenue From Day One",
              text: "Our medical credentialing specialist team supports your revenue cycle from the very start by ensuring payer enrollments are completed on time, preventing billing gaps and income interruptions that new and growing practices cannot afford.",
            },
            {
              title: "Reduced Administrative Burden",
              text: "By managing complex payer enrollment services externally, we free your internal staff from chasing payer portals, tracking application statuses, and managing endless documentation, so your team stays focused on patient care.",
            },
            {
              title: "Compliance-Minded Credentialing Execution",
              text: "We stay fully aligned with NCQA standards, payer-specific requirements, and state licensing regulations so your credentialing and enrollment process remains dependable, organized, audit-ready, and compliant at every stage.",
            },
          ],
        }}
        faq={{
          image: "/img/services-img/credentialing-and-enrollment.webp",
          imageAlt: "Credentialing and enrollment support",
          title: "Why Choose PSP for Credentialing & Enrollment?",
          description:
            "Our credentialing teams are more than just administrators. We are every USA provider's most trusted provider credentialing services partner. Our insurance credentialing services advocates work alongside physicians, group practices, and healthcare organizations to eliminate enrollment delays, maintain active payer status, and ensure uninterrupted billing access across all networks.",
          btnText: "Let's Connect",
        }}
        specialtiesContent={{
          eyebrow: "PAYER NETWORKS & SPECIALTIES",
          heading:
            "Provider Credentialing Services Tailored to Every Payer and Every Specialty.",
          description:
            "Explore the payer specialties we support through dependable insurance credentialing services, organized enrollment workflows, and professional payer enrollment services execution.",
        }}
      />
      <ServiceCTASection
        imageSrc="/img/services-img/cta-section-img.webp"
        title="Dedicated Billing Consultant for Your Clinic’s RCM"
        description="Delays in provider credentialing services can directly interrupt your revenue cycle, prevent billing, and cost your practice thousands of dollars in lost reimbursements every month. PSP's certified medical credentialing specialist team streamlines your entire credentialing and enrollment process from CAQH profile management and primary source verification to payer-specific application submission, status tracking, and re-credentialing, all supported by real-time status updates and detailed reporting so you maintain full visibility and control over every active and pending enrollment at all times."
        btnText="Let's Connect"
      />
    </>
  );
}
